import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { AuthFeil, type AdminAuth, type AdminSesjon, type Aal, type Faktorer } from './typer.ts';

/**
 * Den ene filen som kjenner Supabase (lint-vakt 3).
 *
 * Sesjonen ligger i `localStorage` fra 2026-09-10, så fanen kan lukkes uten at man må
 * logge inn på nytt. Det ser ut som «husk meg», og er det ikke: **8-timersgrensen
 * håndheves SERVER-SIDE og røres ikke.** `AdminTokenValidator.SignedInAt` tar den
 * ELDSTE `timestamp` i tokenets `amr`, så verken en ny TOTP-utfordring eller en
 * token-fornyelse flytter den. Klienten kan altså huske et token som uansett dør innen
 * åtte timer.
 *
 * Kostnaden er presis: tokenet ligger på maskinen og overlever at nettleseren lukkes, i
 * inntil åtte timer. Før lå det i minnet for fanen. Se «K8 mykes opp» i
 * tech-decisions.md — der står også hvorfor «stol på denne nettleseren i 30 dager» ble
 * forkastet.
 *
 * Feilmeldingene er panelets egne. Supabases tekster kan nevne e-post og
 * brukerstatus, og en innloggingsside skal ikke fortelle en fremmed hvilke
 * kontoer som finnes.
 */
/**
 * Hvor sesjonen ligger. Eksportert fordi en standardparameter ikke kan inspiseres, og
 * dette ene ordet ER beslutningen — uten en test på verdien kan den snus tilbake til
 * `sessionStorage` uten at noe blir rødt. Målt: en spion på `Storage.prototype` skilte
 * ikke de to lagringene, så testen sjekker verdien direkte.
 */
export const SESJONSLAGRING: Storage = localStorage;

/**
 * Navnet faktoren får hos leverandøren. Ett navn for appen og ett for enheten, så en
 * avbrutt registrering kan ryddes uten å ta den andre typen med seg.
 */
const FAKTORNAVN_APP = 'Innspel-panelet';
const FAKTORNAVN_ENHET = 'Innspel-panelet (enhet)';

/**
 * Kan nettleseren gjøre en WebAuthn-seremoni? Samme sjekk som leverandørens egen
 * `browserSupportsWebAuthn`, skrevet ut her fordi `@supabase/supabase-js` ikke
 * eksporterer den — og fordi et ja/nei som styrer hva brukeren FÅR SE ikke skal
 * avhenge av en intern sti i en pakke.
 *
 * `window.isSecureContext` er med: WebAuthn finnes ikke over usikker http, og et
 * primærvalg som kaster ved trykk er verre enn ett som aldri vises.
 */
function nettleserenKanEnhet(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function' &&
    typeof navigator.credentials?.get === 'function'
  );
}

export function lagSupabaseAuth(url: string, anonKey: string, lagring: Storage = SESJONSLAGRING): AdminAuth {
  const klient: SupabaseClient = createClient(url, anonKey, {
    auth: {
      storage: lagring,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });

  return {
    async hentSesjon(): Promise<AdminSesjon | null> {
      const { data, error } = await klient.auth.getSession();
      if (error || !data.session) return null;
      const nivå = await klient.auth.mfa.getAuthenticatorAssuranceLevel();
      if (nivå.error || !nivå.data) return null;
      return {
        accessToken: data.session.access_token,
        aal: (nivå.data.currentLevel ?? 'aal1') as Aal,
        nesteAal: (nivå.data.nextLevel ?? 'aal1') as Aal,
        ...(data.session.user.email ? { epost: data.session.user.email } : {}),
      };
    },

    paaEndring(cb) {
      const { data } = klient.auth.onAuthStateChange(() => cb());
      return () => data.subscription.unsubscribe();
    },

    async sendKode(epost) {
      // Sete-først (tech-decisions «Adminbrukere fra panelet»): kontoen opprettes ved første
      // innlogging, og hooken før-bruker-opprettes slipper bare inn adresser med ventende sete.
      // Samme setning uansett svar — verken 4xx fra Supabase eller hookens avslag skal røpe om
      // adressen finnes hos noen (anti-orakel).
      const { error } = await klient.auth.signInWithOtp({ email: epost, options: { shouldCreateUser: true } });
      if (error) throw new AuthFeil('Koden kunne ikke sendes. Sjekk adressen, eller prøv igjen om litt.');
    },

    async bekreftKode(epost, kode) {
      const { error } = await klient.auth.verifyOtp({ email: epost, token: kode.trim(), type: 'email' });
      if (error) throw new AuthFeil('Koden stemmer ikke, eller den er utløpt. Be om en ny.');
    },

    async loggInnMedGoogle() {
      const { error } = await klient.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/innboks` },
      });
      if (error) throw new AuthFeil('Innlogging med Google er ikke tilgjengelig akkurat nå.');
    },

    async startOppsett() {
      // Et avbrutt oppsett etterlater en uverifisert faktor med samme navn; ryddes først.
      const eksisterende = await klient.auth.mfa.listFactors();
      for (const f of eksisterende.data?.all ?? []) {
        if (f.factor_type === 'totp' && f.status === 'unverified') await klient.auth.mfa.unenroll({ factorId: f.id });
      }
      const { data, error } = await klient.auth.mfa.enroll({ factorType: 'totp', friendlyName: FAKTORNAVN_APP });
      if (error || !data) throw new AuthFeil('Oppsettet av autentiseringsapp kunne ikke starte. Prøv igjen.');
      return { faktorId: data.id, qr: data.totp.qr_code, noekkel: data.totp.secret, uri: data.totp.uri };
    },

    async bekreftOppsett(faktorId, kode) {
      const { error } = await klient.auth.mfa.challengeAndVerify({ factorId: faktorId, code: kode.trim() });
      if (error) throw new AuthFeil('Koden stemmer ikke. Vent på neste kode i appen og prøv igjen.');
    },

    async bekreftUtfordring(kode) {
      const { data, error } = await klient.auth.mfa.listFactors();
      const faktor = data?.totp.find((f) => f.status === 'verified');
      if (error || !faktor) throw new AuthFeil('Fant ingen autentiseringsapp på kontoen.');
      const svar = await klient.auth.mfa.challengeAndVerify({ factorId: faktor.id, code: kode.trim() });
      if (svar.error) throw new AuthFeil('Koden stemmer ikke. Vent på neste kode i appen og prøv igjen.');
    },

    /* ----------------------------------------------------------------------------
       ENHETEN (WebAuthn) — Windows Hello, Touch ID, sikkerhetsnøkkel.

       API-et er `klient.auth.mfa.webauthn`, altså MFA-sporet: en verifisert
       enhetsfaktor legger `mfa/webauthn` i tokenets `amr` og løfter sesjonen til aal2
       på nøyaktig samme måte som `mfa/totp`. Det er et ANNET spor enn `auth.passkey.*`
       og `signInWithPasskey`, som er passordløs INNLOGGING — den ville gjort enheten
       til én faktor i stedet for to, og er ikke det vi vil ha.

       EKSPERIMENTELT: hver av disse metodene er merket `@experimental` hos
       leverandøren og kan endres uten varsel i en minor-oppgradering. Risikoen er
       avgrenset til denne filen, og appen står som reserve — ingen mister tilgang om
       sporet må rulles tilbake. Låsen selv er urørt: aal2 kreves som før.

       `rpId` og `rpOrigins` sendes IKKE herfra. Leverandøren fyller dem fra
       `window.location.hostname` og `window.location.origin`, og serveren avviser alt
       som ikke står i prosjektets Relying Party-oppsett. Å hardkode et domene her ville
       lagt en fjerde miljøvariabel på deployen for å si det nettleseren alt vet.
       Oppsettet står i docs/drift/supabase-enhetsfaktor.md.
    ---------------------------------------------------------------------------- */

    stoetterEnhet() {
      return nettleserenKanEnhet();
    },

    async hentFaktorer(): Promise<Faktorer> {
      const { data, error } = await klient.auth.mfa.listFactors();
      if (error || !data) throw new AuthFeil('Vi fikk ikke sjekket hvordan du bekrefter. Last siden på nytt.');
      const verifiserte = (data.all ?? []).filter((f) => f.status === 'verified');
      return {
        app: verifiserte.some((f) => f.factor_type === 'totp'),
        enhet: verifiserte.some((f) => f.factor_type === 'webauthn'),
        // Bare de to typene panelet kjenner. Dukker en tredje opp hos leverandøren, står den
        // ikke i lista — og «Min innlogging» viser heller ingenting enn en rad uten ord.
        liste: verifiserte.flatMap((f) =>
          f.factor_type === 'totp' || f.factor_type === 'webauthn'
            ? [{ id: f.id, type: f.factor_type === 'totp' ? ('app' as const) : ('enhet' as const), lagtTil: f.created_at }]
            : [],
        ),
      };
    },

    async fjernFaktor(id) {
      const { error } = await klient.auth.mfa.unenroll({ factorId: id });
      if (error) throw new AuthFeil('Vi fikk ikke fjernet den. Prøv igjen om litt.');
    },

    async settOppEnhet() {
      // Som TOTP-oppsettet: en avbrutt registrering etterlater en uverifisert faktor,
      // og bare enhetens ryddes — appens uverifiserte tilhører det andre sporet.
      const eksisterende = await klient.auth.mfa.listFactors();
      for (const f of eksisterende.data?.all ?? []) {
        if (f.factor_type === 'webauthn' && f.status === 'unverified') await klient.auth.mfa.unenroll({ factorId: f.id });
      }
      const { error } = await klient.auth.mfa.webauthn.register({ friendlyName: FAKTORNAVN_ENHET });
      if (error) throw new AuthFeil('Enheten bekreftet ikke. Prøv igjen, eller bruk en autentiseringsapp.');
    },

    async bekreftMedEnhet() {
      const { data, error } = await klient.auth.mfa.listFactors();
      const faktor = (data?.all ?? []).find((f) => f.factor_type === 'webauthn' && f.status === 'verified');
      if (error || !faktor) throw new AuthFeil('Fant ingen enhet på kontoen.');
      const svar = await klient.auth.mfa.webauthn.authenticate({ factorId: faktor.id });
      if (svar.error) throw new AuthFeil('Enheten bekreftet ikke. Prøv igjen, eller bruk koden fra appen.');
    },

    async loggUt() {
      await klient.auth.signOut();
    },
  };
}
